import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getCurrentUserId, getDealApplicationId, getDealBorrowerId, getHasAcknowledgedSpanishPrompt, getHasPreferredLanguageOverride, getLoanId, getLoanNumber, getUserRole, selectPreferredLanguage, setHasAcknowledgedSpanishPrompt, setHasPreferredLanguageOverride, setPreferredLanguage } from "features/authSlice";
import { isPostLosLoan, selectHasLoans, selectIsLanguageToggleLocked } from "features/preferredLanguageSlice";
import PostLosLockModal from "./post-los-lock-modal/PostLosLockModal";
import LanguageToggle from "./language-toggle/LanguageToggle";
import EnglishRevertModal from "./english-revert-modal/EnglishRevertModal";
import SpanishAcknowledgmentModal from "components/v2/spanish-acknowledgment-modal/SpanishAcknowledgmentModal";
import { DEFAULT_LANGUAGE, LANG_PREF_COOKIE_NAME, LOCAL_STORAGE_KEY, SPANISH_LANG_CODE, SUPPORTED_LANGUAGES } from "utilities/Constant";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { extractDealServicePreferredLanguage, getPreferredLanguage, toUserDbPreferredLanguage } from "utilities/getPreferredLanguage";
import { useGetBorrowerDetailsQuery, useGetBorrowerIdentifiersQuery, useGetBorrowerPersonalInformationQuery, useUpdateUserPreferredLanguageMutation } from "pages/dashboard/home/dashboardService";
import { RegistrationRoleType } from "pages/registration/registrationService";

interface PreferredLanguageProps {
  readonly isMobile?: boolean;
  readonly isAuthenticated?: boolean;
}

const setLangPrefCookie = (langCode: string): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=${langCode};path=/`;
};

const clearLangPrefCookie = (): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

const setLocalStorage = (key: string, value: string): void => {
  window.localStorage.setItem(key, value);
};

const getLocalStorage = (key: string): string | null => {
  return window.localStorage.getItem(key);
};

const refreshPageForTranslation = (): void => {
  window.location.reload();
};

const shouldReloadForLanguageChange = (nextLanguage: string): boolean => {
  const currentLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
  return currentLanguage !== nextLanguage;
};

const isCoBorrowerRole = (userRole?: string): boolean => {
  return userRole === RegistrationRoleType.SpousalCoBorrower ||
    userRole === RegistrationRoleType.NonSpousalCoBorrower ||
    userRole === RegistrationRoleType.CoBorrower;
};

const PreferredLanguage: React.FC<PreferredLanguageProps> = ({ isMobile = false, isAuthenticated = false }) => {
  const dispatch = useDispatch();
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const { pathname } = useLocation();
  const reduxLanguage = useSelector(selectPreferredLanguage);
  const userId = useSelector(getCurrentUserId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const loanId = useSelector(getLoanId);
  const userRole = useSelector(getUserRole);
  const dealBorrowerId = useSelector(getDealBorrowerId);
  const hasAcknowledgedSpanishPrompt = useSelector(getHasAcknowledgedSpanishPrompt);
  const hasPreferredLanguageOverride = useSelector(getHasPreferredLanguageOverride);
  const hasLoans = useSelector(selectHasLoans);
  const hasApplicationContext = Boolean(dealApplicationId);
  const [updateUserPreferredLanguage] = useUpdateUserPreferredLanguageMutation();
  const [showPostLosLockModal, setShowPostLosLockModal] = useState(false);
  const learnMoreButtonRef = useRef<HTMLButtonElement>(null);
  const currentLoanNumber = useSelector(getLoanNumber);
  const isAllPostLOS = useSelector(selectIsLanguageToggleLocked);
  const isMyLoansPage = pathname.startsWith("/my-loans");
  const isCurrentLoanPostLos = isPostLosLoan(currentLoanNumber);
  const isPostLosLoanContext = isMyLoansPage ? isAllPostLOS : isCurrentLoanPostLos;

  const { data: userProfile, isSuccess: isUserProfileSuccess } = useGetBorrowerDetailsQuery(userId, {
    skip:  !userId,
  });
  const { data: borrowerIdentifiers } = useGetBorrowerIdentifiersQuery(
    { loanId, applicationId: dealApplicationId },
    { skip: !isAuthenticated || !hasApplicationContext || !loanId },
  );
  const resolvedDealBorrowerId = isCoBorrowerRole(userRole)
    ? borrowerIdentifiers?.coBorrowerId ?? dealBorrowerId
    : borrowerIdentifiers?.borrowerId ?? dealBorrowerId;
  const { data: dealServicePersonalInformation } = useGetBorrowerPersonalInformationQuery(
    { borrowerId: resolvedDealBorrowerId || "" },
    { skip: !isAuthenticated || !hasApplicationContext || !resolvedDealBorrowerId },
  );

  const [userLanguage, setUserLanguage] = useState<string>(() => getPreferredLanguage(undefined));
  const [showEnglishRevertModal, setShowEnglishRevertModal] = useState(false);
  const [showAcknowledgmentModal, setShowAcknowledgmentModal] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  useEffect(() => {
     if (!isAuthenticated) {
       const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
       setUserLanguage(getPreferredLanguage(savedLanguage ?? reduxLanguage));
       return;
     }

    const dealServiceLanguage = extractDealServicePreferredLanguage(dealServicePersonalInformation);

    const hasLanguageSource = Boolean(reduxLanguage?.trim()) || Boolean(dealServiceLanguage);
    if (!hasLanguageSource) {
      const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
      setUserLanguage(getPreferredLanguage(savedLanguage ?? undefined));
      return;
    }

    const resolved = getPreferredLanguage(reduxLanguage?.trim() ? reduxLanguage : dealServiceLanguage);

    const currentUserDbLanguage = userProfile?.preferredLanguage;
    const isUserDbLanguageNull =
      isUserProfileSuccess &&
      (currentUserDbLanguage == null || currentUserDbLanguage.trim() === "");
    const shouldPromptSpanishAcknowledgment =
      isAuthenticated &&
      resolved === "Español" && 
      isUserDbLanguageNull &&
      !hasAcknowledgedSpanishPrompt &&
      !showAcknowledgmentModal; 

    if (!shouldPromptSpanishAcknowledgment) {
      const shouldReload = shouldReloadForLanguageChange(resolved);
      setUserLanguage(resolved);
      setLocalStorage(LOCAL_STORAGE_KEY, resolved);

    }

    if (shouldPromptSpanishAcknowledgment) { 
      setPendingLanguage(resolved);
      setShowAcknowledgmentModal(true);
    }
  }, [
    dealServicePersonalInformation,
    reduxLanguage,
    isAuthenticated,
    isUserProfileSuccess,
    userProfile?.preferredLanguage,
    hasPreferredLanguageOverride,
    hasAcknowledgedSpanishPrompt,
    showAcknowledgmentModal,
  ]);

  
  useEffect(() => {
    const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
    if (savedLanguage === "Español") {
      setLangPrefCookie(SPANISH_LANG_CODE);
    } else {
      clearLangPrefCookie();
    }
  }, []);

  if (!featureFlag?.preferredLanguageEnabled) {
    return null;
  }

  const applyLanguageState = (value: string) => {
    setUserLanguage(value);
    setLocalStorage(LOCAL_STORAGE_KEY, value);

    if (isAuthenticated) {
      dispatch(setPreferredLanguage(value));
      dispatch(setHasPreferredLanguageOverride(true));
    }
    if (value === "Español") {
      setLangPrefCookie(SPANISH_LANG_CODE);
    } else {
      clearLangPrefCookie();
    }
  };


  const persistLanguageChange = async (value: string): Promise<boolean> => {
    if (!isAuthenticated || !userId) {
      return false;
    }

    const response = await updateUserPreferredLanguage({
      userId,
      preferredLanguage: toUserDbPreferredLanguage(value),
    });

    return !("error" in response);
  };

  const commitLanguageChange = async (value: string): Promise<void> => {
    const shouldReload = shouldReloadForLanguageChange(value);
    applyLanguageState(value);

    if (!isAuthenticated) {
      if (shouldReload) {
        refreshPageForTranslation();
      }
      return;
    }

    const didPersist = await persistLanguageChange(value);
    if (didPersist && shouldReload) {
      refreshPageForTranslation();
    }
  };

  const handleLanguageChange = (value: string) => {
     if (isPostLosLoanContext) {
      return;
    }
    setPendingLanguage(value);
    if (value == "Español")
    {
      setShowAcknowledgmentModal(true);
    }
    else
    {
       setShowEnglishRevertModal(true);
    }     
  };

  const handleAcknowledge = () => {
    const languageToApply = pendingLanguage ?? "Español";
    setPendingLanguage(null);
    if (isAuthenticated) {
      dispatch(setHasAcknowledgedSpanishPrompt(true));
    }
    setShowAcknowledgmentModal(false);
    void commitLanguageChange(languageToApply);
  };

  const handleRevertConfirm = () => {
    const languageToApply = pendingLanguage ?? DEFAULT_LANGUAGE;
    setPendingLanguage(null);
    setShowEnglishRevertModal(false);
    void commitLanguageChange(languageToApply);
  };

  const handleRevertCancel = () => {
    setPendingLanguage(null);
    setShowEnglishRevertModal(false);
  };
    const handleClosePostLosLockModal = () => {
    setShowPostLosLockModal(false);
  };

  const isDisabled = isAuthenticated ? !!hasLoans : false;

  return (
    <>
      <div className="preferred-language">
        {isPostLosLoanContext && !isMobile && (
          <div className="preferred-language__lock-details">
            <span className="preferred-language__lock-label">Language Locked</span>
            <button
              ref={learnMoreButtonRef}
              type="button"
              className="preferred-language__learn-more-button"
              onClick={() => setShowPostLosLockModal(true)}
            >
              Learn More
            </button>
          </div>
        )}
        <LanguageToggle value={userLanguage} onChange={handleLanguageChange} options={SUPPORTED_LANGUAGES} isMobile={isMobile} disabled={isPostLosLoanContext} onLockedClick={isMobile && isPostLosLoanContext ? () => setShowPostLosLockModal(true) : undefined}/>

      </div>
      {showAcknowledgmentModal && (
        <SpanishAcknowledgmentModal
          onAcknowledge={handleAcknowledge}
          onCancel={() => {
            setShowAcknowledgmentModal(false);
            setPendingLanguage(null);
          }}
        />
      )}
      {showEnglishRevertModal && (
        <EnglishRevertModal onConfirm={handleRevertConfirm} onCancel={handleRevertCancel} />
      )}
      {showPostLosLockModal && (
        <PostLosLockModal onClose={handleClosePostLosLockModal} returnFocusRef={learnMoreButtonRef} />
      )}
    </>
  );
};

export default PreferredLanguage;
